'use client'

import ClockIcon from '@/components/icons/clock-icon'
import Pager from '@/components/search/pager'
import PublicSearchForm from '@/components/search/public-search-form'
import { pickerOptions } from '@/components/search/search-filter-helpers'
import { dayOptions } from '@/helpers/days'
import { cn } from '@/lib/utils'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'
import { useTransition } from 'react'

const MAX_CHAMPIONSHIPS = 12
export default function RacingShell({
  community,
  facets,
  initialSortOrder,
  total,
  children,
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()
  const sortOrder = searchParams.get('sort') || initialSortOrder || 'Upcoming'
  const pageNum = Number(searchParams.get('page')) || 1
  const fields = [
    {
      key: 'platform',
      label: 'Platform',
      multi: true,
      options: pickerOptions(facets?.platforms || []),
    },
    {
      key: 'game',
      label: 'Game',
      searchable: true,
      options: pickerOptions(facets?.games || [], { games: true }),
    },
    {
      key: 'raceType',
      label: 'Race type',
      multi: true,
      options: pickerOptions(['Single Event', 'Championship Event']),
    },
    {
      key: 'car',
      label: 'Cars',
      multi: true,
      searchable: 'long',
      options: pickerOptions(facets?.cars || [], {
        valueKey: 'id',
        labelKey: 'name',
      }),
    },
    {
      key: 'day',
      label: 'Race days',
      multi: true,
      options: pickerOptions(dayOptions, { days: true }),
    },
    {
      key: 'sort',
      label: 'Sort by',
      defaultValue: 'Upcoming',
      options: pickerOptions(['Live', 'Upcoming', 'Finished']),
    },
    { key: 'q', kind: 'text', label: 'Keyword', maxLength: 80 },
  ]
  const applied = Object.fromEntries(
    fields.map((field) => [
      field.key,
      field.multi
        ? searchParams.getAll(field.key)
        : searchParams.get(field.key),
    ]),
  )
  applied.sort = sortOrder
  const encode = (draft) => {
    const params = new URLSearchParams(searchParams)
    params.delete('page')
    for (const field of fields) {
      params.delete(field.key)
      const value = draft[field.key]
      if (Array.isArray(value))
        value.forEach((entry) => params.append(field.key, entry))
      else if (value) params.set(field.key, value)
    }
    return params
  }
  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(page))
    startTransition(() =>
      router.push(`${pathname}?${params}`, { scroll: false }),
    )
  }
  return (
    <div aria-busy={isPending}>
      <PublicSearchForm
        fields={fields}
        applied={applied}
        encode={encode}
        keywordPlaceholder={`Search ${community.name}`}
        className="mt-6"
      />
      {isPending && <p role="status">Updating results…</p>}
      <div className="divide col-span-full mt-6 hidden bg-neutral-blue-700 desktop-xs:block"></div>

      <div
        className={cn(
          'divide mt-6 h-px bg-warning-500 desktop-xs:mt-8',
          sortOrder === 'Live' && 'bg-success-500',
          sortOrder === 'Upcoming' && 'bg-warning-500',
          sortOrder === 'Finished' && 'bg-error-500',
        )}
      />

      <div className="mt-3 flex items-center justify-between desktop-xs:mt-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex size-8 items-center justify-center rounded-[5px] align-middle tablet:size-10 desktop-xs:size-12',
                sortOrder === 'Live' && 'bg-success-500',
                sortOrder === 'Upcoming' && 'bg-warning-500',
                sortOrder === 'Finished' && 'bg-error-500',
              )}
            >
              <ClockIcon className="size-[18px] stroke-white stroke-2 tablet:size-[20px] desktop-xs:size-[26px]" />
            </div>
            <h5 className="text-h-6 font-semibold desktop-xs:text-h-5">
              {sortOrder}
            </h5>
          </div>
          <p className="h-[23px] text-p-2 font-normal text-neutral-blue-200 desktop-xs:h-[25px] desktop-xs:text-p-3">
            {sortOrder === 'Live'
              ? `Browse live championships in the ${community.name} community.`
              : sortOrder === 'Upcoming'
                ? `Browse championships with upcoming events in the ${community.name} community.`
                : `View finished championships in the ${community.name} community.`}
          </p>
        </div>
      </div>

      {children}

      {total > MAX_CHAMPIONSHIPS && (
        <Pager
          className="col-span-full mt-10 desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:max-w-[408px] desktop-xs:justify-self-end"
          page={pageNum}
          pageLimit={Math.ceil(total / MAX_CHAMPIONSHIPS)}
          setPage={handlePageChange}
        />
      )}
    </div>
  )
}
